import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  MenuList,
  OutlinedInput,
  Typography,
} from '@mui/material';
import axios from 'axios';

import { useDebouncedCallback } from '@altirllc/shared/ui/hooks/useDebouncedCallback';
import { useIsUnmounted } from '@altirllc/shared/ui/hooks/useIsUnmounted';

import { partition } from '@/utils/arrayUtils';
import { handleError } from '@/utils/errorUtils';

import { AllCheckbox } from '@/components/AllCheckbox';
import { ChevronRight } from '@/components/icons/ChevronRight';
import { Search } from '@/components/icons/Search';
import { Loader } from '@/components/Loader';

import { MultiSelectItem } from './MultiSelectItem';
import { styles } from './MutliSelect.styles';
import type { TMultiSelectSearchProps, TSelectItem } from './types';

export const MultiSelectSearch = ({
  value,
  toggle,
  getOptions,
  searchPlaceholder,
  selectedTitle,
  showSelected,
}: TMultiSelectSearchProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<TSelectItem[]>([]);

  const unmounted = useIsUnmounted();
  const abort = useRef<AbortController>();
  const optionsRef = useRef<HTMLUListElement>(null);
  const valueIds = useMemo(() => value.map(({ id }) => id), [value]);

  const [selected, available] = partition(options, ({ id }) => valueIds.includes(id));
  const allSelected = available.length === 0;
  const someSelected = selected.length > 0;

  const search = (query = '') => {
    setLoading(true);
    abort.current?.abort();

    // eslint-disable-next-line compat/compat -- AbortController is supported by every browser the portal targets (modern evergreen browsers); only iOS Safari 11.0-11.2 lacks it
    abort.current = new AbortController();
    getOptions(query, abort.current.signal)
      .then((newOptions) => {
        if (!unmounted.current) {
          setLoading(false);
          setOptions(newOptions);
        }
      })
      .catch((error) => {
        handleError(error, 'UNHANDLED_RESPONSE');

        if (!axios.isCancel(error) && !unmounted.current) {
          setLoading(false);
          setOptions([]);
        }
      });
  };

  const searchDebounced = useDebouncedCallback(search, [getOptions, unmounted], 200);

  // Select what options left if there are any. Unselect all otherwise
  const toggleAll = () => {
    if (available.length > 0) {
      toggle(available);
    } else {
      toggle(selected);
    }
  };

  useEffect(search, [getOptions, unmounted]);

  useEffect(() => {
    const container = optionsRef.current as HTMLElement;
    const onScroll = () => {
      window.requestAnimationFrame(() => {
        container.dataset.scrolled = `${container.scrollTop > 0}`;
      });
    };

    container.scroll(0, 0);
    container.addEventListener('scroll', onScroll);

    return () => {
      container.removeEventListener('scroll', onScroll);
      container.dataset.scrolled = 'false';
    };
  }, [options]);

  return (
    <>
      <FormControl margin="none" sx={styles.search}>
        <OutlinedInput
          sx={styles.searchInput}
          placeholder={searchPlaceholder}
          onChange={({ currentTarget }) => searchDebounced(currentTarget.value)}
          startAdornment={
            <InputAdornment position="start" sx={styles.searchIcon}>
              <Search />
            </InputAdornment>
          }
          autoFocus
          fullWidth
        />
      </FormControl>

      <Box sx={styles.options}>
        <Loader position="absolute" active={loading} />

        {options.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={styles.nothingFound}>
            {t('common.nothingFound')}
          </Typography>
        )}

        <MenuList ref={optionsRef} sx={styles.optionsList}>
          {options.length > 0 && (
            <MenuItem onClick={toggleAll} sx={[styles.item, styles.selectAll]}>
              <AllCheckbox checked={someSelected} all={allSelected} sx={styles.itemCheckbox} />
              <Typography fontWeight={500}>{t('common.selectAll')}</Typography>
            </MenuItem>
          )}

          {options.map((item) => (
            <MultiSelectItem
              key={item.id}
              item={item}
              toggle={() => toggle([item])}
              selected={valueIds.includes(item.id)}
            />
          ))}
        </MenuList>
      </Box>

      {value.length > 0 && (
        <Box onClick={showSelected} sx={styles.showSelected}>
          <Typography fontWeight={500}>
            {selectedTitle} ({value.length})
          </Typography>

          <ChevronRight />
        </Box>
      )}
    </>
  );
};
